import React, { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Moon, Sun } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import logoImageDark from '../../assets/meaningful_logo_dark.png';
import logoImageLight from '../../assets/meaningful_logo_light.png';
import './../../css/Home.css';

const Home = () => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const logoImage = theme === 'light' ? logoImageLight : logoImageDark;

  useEffect(() => {
    window.history.replaceState(null, '', '/');
  }, []);

  const handleGetStarted = useCallback((e) => {
    e.preventDefault();
    navigate('/login', { replace: true });
  }, [navigate]);

  return (
    <div className="home-container">
      <Helmet>
        <title>Meaningful: the personal CRM for the people who matter most.</title>
        <meta name="description" content="A private companion for your personal network featuring voice notes, private AI summaries, and interactive connection maps. Free during alpha." />
        <meta name="keywords" content="personal CRM, contact manager, relationship management, follow-up reminders, professional networking tool, private AI, social capital" />
        <link rel="canonical" href="https://someaningful.com/" />
        <meta property="og:title" content="Meaningful: the personal CRM for the people who matter most." />
        <meta property="og:description" content="A private companion for your personal network featuring voice notes, private AI summaries, and interactive connection maps. Free during alpha." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://someaningful.com/" />
        <meta property="og:image" content="https://someaningful.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="robots" content="index, follow" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Meaningful: the personal CRM for the people who matter most." />
        <meta name="twitter:description" content="A private companion for your personal network featuring voice notes, private AI summaries, and interactive connection maps. Free during alpha." />
        <meta name="twitter:image" content="https://someaningful.com/og-image.png" />
      </Helmet>
      
      {/* Hero Section */}
      <div className="hero-wrapper">
        <button
          className="theme-toggle-btn"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          aria-label="Toggle dark/light mode"
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
        </button>
        <div className="hero-section">
          <div className="brand-mark">
            <img
              src={logoImage}
              alt="Meaningful — personal CRM logo"
              className="brand-logo"
            />
          </div>
          <h1 className="sr-only">Meaningful — Personal CRM and Relationship Manager</h1>
          <p className="title" aria-hidden="true">meaningful</p>
          <div className="tagline-container">
            <p className="tagline">stay in touch</p>
            <div className="tagline-details">
              <span>personal CRM</span><span>•</span><span>contact manager</span><span>•</span><span>strategic networking</span>
            </div>
          </div>
          <button onClick={handleGetStarted} className="cta-button">
            Get Started
            <ArrowRight size={20} />
          </button>
        </div>
      </div>

      {/* Content Flow - Seamless Storytelling */}
      <div className="content-flow">
        
        {/* Opening Statement */}
        <section className="opening-section">
          <div className="content-card philosophy-card">
            <div className="card-accent"></div>
            <p className="opening-text">
              The people you know and interact with shape your wellbeing. You thrive with strong relationships.
            </p>
          </div>
        </section>

        {/* Core Message */}
        <section className="core-section">
          <div className="content-card core-card">
            <h2 className="section-title">A personal CRM for people that you enjoy spending time with.</h2>
            <p className="core-text">
              Write down the last time you spoke, what you talked about. Note what mattered — something they mentioned, a commitment you made, their kid's birthday. Get a nudge when someone's been quiet too long.
            </p>
            <p className="core-subtext">
              Everything stays with you. Your contacts aren't a product. We just give you the paper.
            </p>
          </div>
        </section>

        {/* The Method */}
        <section className="method-section">
          <div className="content-card method-card">
            <h2 className="section-title">How it works</h2>
            
            <div className="method-grid">
              <div className="method-item">
                <div className="method-number">01</div>
                <h3 className="method-name"><strong>connect</strong> with context</h3>
                <p className="method-description">
                  Add people. Log conversations. Note what matters — a shared interest, a commitment you made, something they mentioned in passing. The small details are what make follow-ups feel genuine.
                </p>
              </div>

              <div className="method-item">
                <div className="method-number">02</div>
                <h3 className="method-name"><strong>remember</strong> what counts</h3>
                <p className="method-description">
                  Birthdays, last contact dates, open threads. Meaningful surfaces what you'd otherwise forget — not because you don't care, but because life moves fast and memory can only handle so much.
                </p>
              </div>

              <div className="method-item">
                <div className="method-number">03</div>
                <h3 className="method-name"><strong>show up</strong> consistently</h3>
                <p className="method-description">
                  Follow up. Check in. Congratulate. Relationships don't fade because people stop caring — they fade because you drift.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Invitation */}
        <section className="invitation-section">
          <div className="content-card invitation-card">
            <div className="invitation-content">
              <h2 className="invitation-title">Free during alpha — try it today</h2>
              <p className="invitation-text">
                All features unlocked, no credit card needed. Join early and help shape where it goes.
              </p>
              <div className="cta-wrapper">
                <button onClick={handleGetStarted} className="cta-button primary">
                  Check it out
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer v2.0 - Premium */}
        <footer className="footer-section-v2">
          <div className="footer-content-v2">

            {/* Footer Brand */}
            <div className="footer-brand">
              <div className="footer-logo">
                <img
                  src={logoImage}
                  alt="Meaningful — personal CRM"
                  className="footer-brand-logo"
                />
                <span className="footer-brand-text">meaningful</span>
              </div>
              <p className="footer-tagline">for people who care</p>
            </div>

            {/* Footer Navigation */}
            <div className="footer-nav">
              <div className="footer-nav-section">
                <h4 className="footer-nav-title">Product</h4>
                <div className="footer-nav-links">
                  <Link to="/features" className="footer-nav-link">Features</Link>
                  <Link to="/use-cases" className="footer-nav-link">Use Cases</Link>
                  <Link to="/pricing" className="footer-nav-link">Pricing</Link>
                  <Link to="/demo" className="footer-nav-link">Demo</Link>
                  <Link to="/changelog" className="footer-nav-link">Changelog</Link>
                </div>
              </div>

              <div className="footer-nav-section">
                <h4 className="footer-nav-title">Company</h4>
                <div className="footer-nav-links">
                  <Link to="/about" className="footer-nav-link">About</Link>
                  <Link to="/blog" className="footer-nav-link">Blog</Link>
                  <Link to="/contact" className="footer-nav-link">Contact</Link>
                  <Link to="/security" className="footer-nav-link">Security</Link>
                </div>
              </div>

              <div className="footer-nav-section">
                <h4 className="footer-nav-title">Legal</h4>
                <div className="footer-nav-links">
                  <Link to="/privacy" className="footer-nav-link">Privacy Policy</Link>
                  <Link to="/cookie-policy" className="footer-nav-link">Cookie Policy</Link>
                  <Link to="/terms" className="footer-nav-link">Terms of Service</Link>
                </div>
              </div>
            </div>

            {/* Footer Bottom */}
            <div className="footer-bottom">
              <div className="footer-divider"></div>
              <div className="footer-bottom-content">
                <p className="footer-copyright">
                  © 2026 Meaningful. Crafted with precision in Den Haag, Netherlands.
                </p>
                <div className="footer-status">
                  <span className="footer-status-indicator"></span>
                  <span className="footer-status-text">Alpha Build</span>
                </div>
              </div>
            </div>

          </div>
        </footer>

      </div>
    </div>
  );
};

export default Home;